import { useState, useEffect, useContext } from 'react';
import { YoutubeService } from '../services';
import { TranscriptionDialogContext } from '../context';
import {
  extractDouyinId,
  extractInstagramId,
  extractTikTokId,
  extractYoutubeId,
  isDouyinUrl,
  isInstagramUrl,
  isTikTokUrl,
  isYoutubeUrl,
  PlatformType,
  isYoutubeShortUrl,
} from '../utils/videoHelper';

const fetchOembed = async (url: string, oembed: string) => {
  const res = await fetch(`${oembed}?url=${encodeURIComponent(url)}`);
  if (!res.ok) throw new Error(`${oembed} oEmbed ${res.status}`);
  const data = await res.json();
  return data;
};

const useVideoInfo = (url: string) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [id, setId] = useState<string | null>(null);
  const [platform, setPlatform] = useState<PlatformType | null>(null);
  const { setApriori, setVideoUrl } = useContext(TranscriptionDialogContext);

  useEffect(() => {
    let isMounted = true;

    const fetchInfo = async () => {
      if (!url) return;
      setLoading(true);
      setError(null);

      try {
        // YouTube
        if (isYoutubeUrl(url)) {
          setPlatform(PlatformType.youtube);
          const ytId = extractYoutubeId(url) as (typeof PlatformType)[keyof typeof PlatformType];
          setId(ytId);

          const data = await YoutubeService.getVideoInfo(url);
          if (isMounted && data?.items[0]) {
            setApriori({
              title: data.items[0]?.snippet.title || `My Song ${new Date().toLocaleString()}`,
              composer: data.items[0]?.snippet.channelTitle || 'unknown',
            });
          }
          if(isYoutubeShortUrl(url)) {
            setVideoUrl(`https://www.youtube.com/shorts/${ytId}`)
          } else {
            setVideoUrl(`https://www.youtube.com/watch?v=${ytId}`)
          }
          
        } else if (isTikTokUrl(url)) {
          let oembed = 'https://www.tiktok.com/oembed';
          setPlatform(PlatformType.tiktok);
          const tkId = extractTikTokId(url);
          setId(tkId);

          const res = await fetchOembed(url, oembed);
          if (isMounted) {
            setApriori({
              title: res.title || `My Song ${new Date().toLocaleString()}`,
              composer: res.author_unique_id || res.author_name || 'unknown',
            });
          }
          setVideoUrl(`https://www.tiktok.com/${res.author_unique_id}/video/${tkId}`);
        } else if (isDouyinUrl(url)) {
          setPlatform(PlatformType.douyin);
          const tkId = extractDouyinId(url);
          setId(tkId);
        } else if (isInstagramUrl(url)) {
          setPlatform(PlatformType.instagram);
          const igId = extractInstagramId(url);
          setId(igId);
          setVideoUrl(`https://www.instagram.com/reel/${igId}`);
          // TODO: throws forbidden error
          // const res = await fetch(
          //   `https://graph.facebook.com/v19.0/instagram_oembed?url=${encodeURIComponent(
          //     url,
          //   )}&omitscript=true`,
          // );
          // if (!res.ok) throw new Error(`Instagram oEmbed ${res.status}`);
          // const data = await res.json();
          // if (isMounted) {
          //   setApriori({
          //     title: data.title || `My Song ${new Date().toLocaleString()}`,
          //     composer: data.author_name || 'unknown',
          //   });
          // }
        } else {
          throw new Error('Unsupported URL');
        }
      } catch (e: any) {
        if (isMounted) {
          setError(e.message || 'Failed to fetch video info');
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchInfo();
    return () => {
      isMounted = false;
    };
  }, [url, setApriori]);

  return { loading, error, id, platform };
};

export default useVideoInfo;
